import React from "react";
import { FileViewerContainer } from "components/common";
import { ImageMeta } from "models/propTypes/fileViewerPropTypes";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
import { useIncomingInvoiceService } from "serviceHooks";

function useIncomingInvoiceFileViewer(
  invoiceId?: number,
  invoiceAttachments?: WintSuperkollAPIModel.IncomingInvoiceAttachment[],
  onDownloadClick?: (index: number) => void
) {
  const incomingInvoiceService = useIncomingInvoiceService();
  const attachments: ImageMeta[] =
    invoiceId && invoiceAttachments
      ? invoiceAttachments?.map((meta) => ({
          id: meta.id,
          contentType: meta.contentType,
          numberOfPages: 1,
          data: meta.data,
          loading: (meta as unknown as ImageMeta).loading,
        }))
      : [];

  const fileCache = React.useRef(new Map<string, Promise<string>>());

  const getFile = (invoiceId: number, attachmentId: number | string) => {
    // The same file is requested again by every viewer that mounts (e.g. a dialog beside the page).
    const cacheKey = `${invoiceId}-${attachmentId}`;
    const cached = fileCache.current.get(cacheKey);
    if (cached) return cached;
    const request = incomingInvoiceService.getAttachment(invoiceId, attachmentId);
    fileCache.current.set(cacheKey, request);
    request.catch(() => fileCache.current.delete(cacheKey));
    return request;
  };

  const fileViewerComponent = React.useMemo(
    () => (
      <FileViewerContainer
        key={invoiceId}
        meta={attachments}
        parentId={invoiceId ?? 0}
        onDownloadClick={onDownloadClick}
        loading={invoiceId === undefined}
        getFile={invoiceId !== undefined ? getFile.bind(null, invoiceId) : undefined}
      />
    ),
    [attachments, invoiceId]
  );

  return fileViewerComponent;
}

export default useIncomingInvoiceFileViewer;
